import React, { useCallback, useMemo, useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  FormGroup,
  Input,
  Label,
  Col,
  Row
} from 'reactstrap';
import Button from './Button';
import Icon from './Icon';
import useCompany from '../hooks/useCompany';
import { useApiPost } from '../hooks/useApi';
import { useToast } from '../hooks/useToast';

type Props = {
  open: boolean;
  onClose: () => void;
  carWashId: number;
  onSuccess: () => void;
};

const ModalAddCheckList = ({ open, onClose, carWashId, onSuccess }: Props) => {
  const { addToast } = useToast();
  const company = useCompany();
  const [checkListMarked, setCheckListMarked] = useState<any>({});
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);

  const { mutate, isPending } = useApiPost(
    `/check-list/${carWashId.toString()}`
  );

  const checkListItems = useMemo(
    () =>
      company?.items_check_list?.map((item) => ({
        id: item.id,
        label: item.item,
        checked: !!checkListMarked[item.id!]
      })) || [],
    [company, checkListMarked]
  );

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPhotos(Array.from(e.target.files));
    }
  };

  const handleSubmit = useCallback(() => {
    const formData = new FormData();
    formData.append('carwash', carWashId.toString());
    formData.append('note', notes);

    // Add checked items
    const selectedItems = Object.keys(checkListMarked).filter(
      (key) => checkListMarked[key]
    );
    selectedItems.forEach((itemId) => {
      formData.append('items', itemId);
    });

    // Add photos (backend expects 'images' field)
    photos.forEach((photo) => {
      formData.append('images', photo);
    });

    mutate(formData, {
      onSuccess: () => {
        addToast({
          message: 'Checklist added successfully',
          color: 'success',
          icon: 'check'
        });
        // Reset form
        setCheckListMarked({});
        setNotes('');
        setPhotos([]);
        onSuccess();
        onClose();
      },
      onError: (error: any) => {
        addToast({
          message: error?.message || 'Failed to add checklist',
          color: 'danger',
          icon: 'alert-circle'
        });
      }
    });
  }, [
    carWashId,
    notes,
    checkListMarked,
    photos,
    mutate,
    addToast,
    onSuccess,
    onClose
  ]);

  return (
    <Modal isOpen={open} toggle={onClose} size="lg">
      <ModalBody>
        <div className="d-flex justify-content-between align-items-center w-100 mb-3">
          <span className="fw-bolder h4">Add New Checklist</span>
          <Button
            color="light"
            className="text-primary"
            onClick={onClose}
            size="lg"
          >
            <Icon local icon="close" width={20} />
          </Button>
        </div>

        <Row>
          <Col md={8}>
            <div className="mb-3">
              <Label>Attach Photos</Label>
              <Input
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoChange}
              />
              {photos.length > 0 && (
                <div className="mt-2 d-flex flex-wrap gap-2">
                  {photos.map((photo, index) => (
                    <div
                      key={index}
                      className="position-relative"
                      style={{ width: '100px', height: '100px' }}
                    >
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`Preview ${index + 1}`}
                        className="w-100 h-100 object-fit-cover rounded"
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0"
                        style={{ padding: '2px 6px' }}
                        onClick={() =>
                          setPhotos(photos.filter((_, i) => i !== index))
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Col>

          <Col md={4} className="bg-light py-3 rounded">
            <p className="text-secondary fw-bold mb-3">Check-List Items</p>
            <div className="w-100">
              {checkListItems?.map((item) => (
                <div
                  key={item.id}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <Label check for={`add-check-item-${item.id}`}>
                    {item.label}
                  </Label>
                  <FormGroup switch className="d-inline-block mb-0">
                    <Input
                      type="switch"
                      role="switch"
                      checked={item.checked}
                      name={`add-check-item-${item.id}`}
                      id={`add-check-item-${item.id}`}
                      onChange={() =>
                        setCheckListMarked((prev: any) => ({
                          ...prev,
                          [item.id!]: !item.checked
                        }))
                      }
                    />
                  </FormGroup>
                </div>
              ))}
              {(!checkListItems || checkListItems.length === 0) && (
                <p className="small text-secondary">No check-list items</p>
              )}
            </div>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <Label>Quality Control Notes</Label>
            <Input
              type="textarea"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Type here the notes of quality control"
            />
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="border-0">
        <div className="d-flex justify-content-between align-items-center w-100">
          <Button
            outline
            color="danger"
            onClick={onClose}
            type="button"
            size="lg"
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            color="primary"
            onClick={handleSubmit}
            loading={isPending}
            disabled={isPending}
          >
            Add Checklist
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ModalAddCheckList;
